module Sm.Core.Date

open System
open Fable.Core
open Fable.Core.JsInterop

open Sm.Core.Text

let inline dateLocal y m d =
    DateTime(y, m, d)

let inline dateUtc y m d =
    DateTime(y, m, d, 0, 0, 0, DateTimeKind.Utc)

type TimeSpan with
    static member OneSecond = TimeSpan.FromSeconds(1)

    static member OneMinute = TimeSpan.FromMinutes(1)

    static member OneHour = TimeSpan.FromHours(1)

    static member OneDay = TimeSpan.FromDays(1)

    member this.ClockFormat() =
        let inline optional x=
            match x with
            | 0 -> None
            | x -> Some (sprintf "%02i" x)

        let inline required x =
            match optional x with
            | None -> Some "00"
            | x -> x

        let value =
            [
                optional this.Hours
                required this.Minutes
                required this.Seconds
            ]
            |> List.choose id
            |> join ":"

        let sign = if this < TimeSpan.Zero then "-" else ""
        sign + value

    member this.DurationFormat() =
        [
            let h = (int << floor) this.TotalHours in if h > 0 then $"{h}h"
            let m = this.Minutes in if m > 0 then $"{m}m"
        ]
        |> join " "

type DateOnly with
    static member inline Today =
        DateOnly.FromDateTime(DateTime.Today)

    member inline this.DateTime =
        emitJsExpr<DateTime> this "$0"

    member this.IsToday =
        let today = DateTime.Today
        this.Year = today.Year && this.Month = today.Month && this.Day = today.Day

type DateTime with
    member d.HasElapsed(time: TimeSpan) =
        let now = DateTime.Now
        let elapsed = now - d
        elapsed >= time

type Weekday =
    | Monday
    | Tuesday
    | Wednesday
    | Thursday
    | Friday
    | Saturday
    | Sunday

module Weekday =
    let shortName = function
        | Monday -> "Mon"
        | Tuesday -> "Tue"
        | Wednesday -> "Wed"
        | Thursday -> "Thu"
        | Friday -> "Fri"
        | Saturday -> "Sat"
        | Sunday -> "Sun"

    let fromOrdinal = function
        | 1 -> Some Monday
        | 2 -> Some Tuesday
        | 3 -> Some Wednesday
        | 4 -> Some Thursday
        | 5 -> Some Friday
        | 6 -> Some Saturday
        | 7 -> Some Sunday
        | _ -> None

    let toOrdinal = function
        | Monday -> 1
        | Tuesday -> 2
        | Wednesday -> 3
        | Thursday -> 4
        | Friday -> 5
        | Saturday -> 6
        | Sunday -> 7

    let fromDate (date: DateTime) =
        match date.DayOfWeek with
        | DayOfWeek.Monday -> Monday
        | DayOfWeek.Tuesday -> Tuesday
        | DayOfWeek.Wednesday -> Wednesday
        | DayOfWeek.Thursday -> Thursday
        | DayOfWeek.Friday -> Friday
        | DayOfWeek.Saturday -> Saturday
        | DayOfWeek.Sunday -> Sunday
        | _ -> Monday

let startOfWeek date =
    let rec loop (d: DateOnly) =
        if d.DayOfWeek = DayOfWeek.Monday then d
        else
            let prev = d.AddDays(-1)
            loop prev

    loop date

let sameMonth (a: DateOnly) (b: DateOnly) =
    a.Year = b.Year && a.Month = b.Month

module private DateFns =
    type GetWeeksInMonthsOptions =
        abstract weekStartsOn: DayOfWeek

    [<ImportMember("date-fns")>]
    let getWeeksInMonth (date: DateTime) options = jsNative<int>

let getWeeksInMonth date =
    let options =
        { new DateFns.GetWeeksInMonthsOptions with
            member _.weekStartsOn = DayOfWeek.Monday
        }
    DateFns.getWeeksInMonth date options
